<template>
  <div class="wizard">
    <div class="stepper">
      <div class="stepper-items">
        <div
          v-for="s in steps"
          :key="s.step"
          class="stepper-item"
          :class="{
            active: currentStep === s.step,
            done: currentStep > s.step,
            reached: currentStep >= s.step,
            clickable: currentStep > s.step
          }"
          @click="onStepClick(s.step)"
        >
          <div class="step-circle">{{ s.step }}</div>
          <div class="step-label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <CreateSiteStep1
      v-if="currentStep === 1"
      :mode="mode"
      :form-value="form"
      @next="next"
    />

    <CreateSiteStep2
      v-else-if="currentStep === 2"
      :mode="mode"
      :form-value="form"
      @back="back"
      @next="next"
    />

    <CreateSiteStep3
      v-else-if="currentStep === 3"
      :form-value="form"
      @back="back"
      @next="next"
      :sensorItems="sensorItems"
    />

    <CreateSiteStep4
      v-else-if="currentStep === 4"
      :mode="mode"
      :form-value="form"
      @back="back"
      @next="next"
    />

    <CreateSiteStep5
      v-else-if="currentStep === 5"
      :mode="mode"
      :form-value="form"
      @back="back"
      @next="next"
    />
  </div>
</template>

<script>
import api from "@/api/axios"
import CreateSiteStep1 from "./CreateSiteStep1.vue"
import CreateSiteStep2 from "./CreateSiteStep2.vue"
import CreateSiteStep3 from "./CreateSiteStep3.vue"
import CreateSiteStep4 from "./CreateSiteStep4.vue"
import CreateSiteStep5 from "./CreateSiteStep5.vue"

export default {
  name: "CreateSiteWizard",

  components: {
    CreateSiteStep1,
    CreateSiteStep2,
    CreateSiteStep3,
    CreateSiteStep4,
    CreateSiteStep5,
  },

  props: {
    siteId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      currentStep: 1,
      steps: [
        { step: 1, label: "기본 정보" },
        { step: 2, label: "도면 등록" },
        { step: 3, label: "센서 등록" },
        { step: 4, label: "버튼 등록" },
        { step: 5, label: "완료" },
      ],

      form: {
        siteId: null,

        agencyId: null,
        agencyName: "",
        clientId: null,
        clientName: "",
        siteName: "",
        siteEnglishName: "",
        address: "",
        addressDetail: "",
        startDate: "",
        endDate: "",
        manager: "",
        phone: "",

        siteImages: [],
        deletedSiteImageUuids: [],
        siteImageUploadSessionId: null,

        drawing: null,
        drawingAttachFileNo: null,
        drawingUuid: null,
        drawingDeleted: false,

        loggers: [],
        sensors: [],

        buttons: [],

        doneFlag: false,
      },

      sensorItems: [
        { id: 1, name: "경사계", type: "경사계" },
        { id: 2, name: "균열계", type: "균열계" },
        { id: 3, name: "내공변위계", type: "내공변위계" },
        { id: 4, name: "지중경사계", type: "지중경사계" },
        { id: 5, name: "레일변위계", type: "레일변위계" },
        { id: 6, name: "수위계", type: "수위계" },
        { id: 7, name: "유량계", type: "유량계" },
        { id: 8, name: "송유계", type: "송유계" },
        { id: 9, name: "진동계", type: "진동계" },
        { id: 10, name: "무선 데이터로거", type: "무선 데이터로거" },
        { id: 11, name: "와이어 센서", type: "와이어 센서" },
      ],
    }
  },

  computed: {
    mode() {
      return this.$route.query.mode === "edit" ? "edit" : "create"
    },

    isEditMode() {
      return this.mode === "edit"
    },

    normalizedSiteId() {
      const routeValue = this.$route.query.siteId
      if (routeValue !== undefined && routeValue !== null && routeValue !== "") {
        const parsed = Number(routeValue)
        return Number.isNaN(parsed) ? null : parsed
      }

      if (this.siteId !== undefined && this.siteId !== null && this.siteId !== "") {
        const parsed = Number(this.siteId)
        return Number.isNaN(parsed) ? null : parsed
      }

      return null
    },
  },

  watch: {
    "$route.query": {
      immediate: true,
      async handler() {
        await this.initializeWizard()
      },
    },

    siteId: {
      async handler() {
        await this.initializeWizard()
      },
    },
  },

  methods: {
    createEmptyForm() {
      return {
        siteId: null,

        agencyId: null,
        agencyName: "",
        clientId: null,
        clientName: "",
        siteName: "",
        siteEnglishName: "",
        address: "",
        addressDetail: "",
        startDate: "",
        endDate: "",
        manager: "",
        phone: "",

        siteImages: [],
        deletedSiteImageUuids: [],
        siteImageUploadSessionId: null,

        drawing: null,
        drawingAttachFileNo: null,
        drawingUuid: null,
        drawingDeleted: false,

        loggers: [],
        sensors: [],

        buttons: [],

        doneFlag: false,
      }
    },

    resetForm() {
      this.form = this.createEmptyForm()
      this.currentStep = 1
    },

    async initializeWizard() {
      this.resetForm()

      if (!this.isEditMode || !this.normalizedSiteId) {
        return
      }

      try {
        this.loading = true

        const response = await api.get("/app/site/detail", {
          params: {
            id: this.normalizedSiteId,
            deleted: false,
          },
        })

        const detail = response.data?.result
        if (!detail) return

        this.form.siteId = detail.id ?? null
        this.form.agencyId = detail.agencyId ?? null
        this.form.agencyName = detail.agencyName ?? ""
        this.form.clientId = detail.clientId ?? null
        this.form.clientName = detail.clientName ?? ""
        this.form.siteName = detail.siteName ?? ""
        this.form.siteEnglishName = detail.siteEnglishName ?? ""
        this.form.address = detail.address ?? ""
        this.form.addressDetail = detail.addressDetail ?? ""
        this.form.manager = detail.manager ?? ""
        this.form.phone = detail.phone ?? ""
        this.form.drawingAttachFileNo = detail.drawingAttachFileNo ?? null

        if (detail.siteStartAt) {
          this.form.startDate = String(detail.siteStartAt).slice(0, 10)
        }

        if (detail.siteEndAt) {
          this.form.endDate = String(detail.siteEndAt).slice(0, 10)
        }

        const rawImages = detail.siteMainImageList || []

        this.form.siteImages = rawImages.map((item, index) => ({
          uuid: `existing-${item.attachFileNo}-${index}`,
          attachFileNo: item.attachFileNo,
          previewUrl: item.fileUrl,
          originalFileName: item.originalFileName || item.fileName || "기존 이미지",
          sortOrder: item.sortOrder ?? index + 1,
          isNew: false,
          deleted: false,
          file: null,
        }))
      } catch (e) {
        console.error("initializeWizard error", e)
      } finally {
        this.loading = false
      }
    },

    next(payload) {
      this.form = {
        ...this.form,
        ...payload,
      }

      if (this.currentStep < this.steps.length) {
        this.currentStep += 1
      }
    },

    back(payload) {
      if (payload) {
        this.form = {
          ...this.form,
          ...payload,
        }
      }

      if (this.currentStep > 1) {
        this.currentStep -= 1
      }
    },

    async onStepClick(step) {
      if (step >= this.currentStep) return

      if (this.isEditMode && this.normalizedSiteId && step === 1) {
        const ok = window.confirm("기본 정보 화면으로 이동하시겠습니까?")
        if (!ok) return
      }

      this.currentStep = step
    },
  },
}
</script>

<style scoped>
.wizard {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #f8fafc;
  border-radius: 16px;
  overflow: hidden;
}

.stepper {
  position: relative;
  padding: 28px 40px 20px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.stepper-items {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0;
}

.stepper-item {
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: default;
}

.stepper-item.clickable {
  cursor: pointer;
}

.stepper-item:not(:first-child)::before {
  content: "";
  position: absolute;
  top: 20px;
  left: calc(-50% + 21px);
  width: calc(100% - 42px);
  height: 2px;
  background: #e5e7eb;
  z-index: 0;
}

.stepper-item.reached:not(:first-child)::before {
  background: var(--color-primary);
}

.step-circle {
  position: relative;
  z-index: 1;
  width: 42px;
  height: 42px;
  border-radius: 999px;
  background: #d1d5db;
  color: #ffffff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-label {
  margin-top: 10px;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
}

.stepper-item.active .step-circle,
.stepper-item.done .step-circle {
  background: var(--color-primary);
}

.stepper-item.active .step-label {
  color: var(--color-primary);
  font-weight: 700;
}

.stepper-item.done .step-label {
  color: #111827;
}

@media (max-width: 960px) {
  .stepper {
    padding: 24px 18px 16px;
  }

  .step-label {
    font-size: 12px;
  }

  .stepper-item:not(:first-child)::before {
    left: calc(-50% + 21px);
    width: calc(100% - 42px);
  }
}
</style>